import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { leaderAPI } from '../utils/api';
import MemberReportCard from '../components/MemberReportCard';
import SendSummaryPanel from '../components/SendSummaryPanel';
import NotificationHistoryPanel from '../components/NotificationHistoryPanel';
import TeamAnalyticsPanel from '../components/TeamAnalyticsPanel';
import ExportDialog from '../components/ExportDialog';

/**
 * Leader Dashboard — lets team leaders (and admins) view their team's
 * daily reports, browse by date, and inspect per-member history.
 *
 * Flow:
 *  1. On mount: load today's team reports
 *  2. Date picker: reload team reports for selected date
 *  3. "History" button on a member card: slide in a history panel
 */
const LeaderDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // --- state ---
  const [selectedDate, setSelectedDate] = useState('');
  const [teamData, setTeamData] = useState(null);     // { team, date, summary, members }
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // History drawer
  const [historyMember, setHistoryMember] = useState(null);  // member object
  const [historyData, setHistoryData]     = useState(null);  // { member, reports }
  const [historyLoading, setHistoryLoading] = useState(false);

  // Admin: list of teams to pick from
  const [teams, setTeams]       = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');

  // Right panel section toggle: 'history', 'notifications', or 'send'
  const [rightPanelSection, setRightPanelSection] = useState('send');

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [exportDateTo, setExportDateTo] = useState(new Date().toISOString().split('T')[0]);

  // ----------------------------------------------------------------
  // Load team reports for a date
  // ----------------------------------------------------------------
  const loadTeamReports = useCallback(async (date, teamId) => {
    setLoading(true);
    setError('');
    try {
      const res = await leaderAPI.getTeamReports(date || undefined, teamId || undefined);
      setTeamData(res.data);
      if (!selectedDate) setSelectedDate(res.data.date);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load team reports');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Load teams list for admin
  const loadTeams = useCallback(async () => {
    try {
      const res = await leaderAPI.getAllTeams();
      setTeams(res.data.teams);
      if (res.data.teams.length > 0 && !selectedTeamId) {
        setSelectedTeamId(String(res.data.teams[0].id));
      }
    } catch {
      // Non-critical
    }
  }, [selectedTeamId]);

  // Initial load
  useEffect(() => {
    if (user?.role === 'admin') {
      loadTeams();
    }
  }, [user, loadTeams]);

  useEffect(() => {
    // For leaders load immediately; for admin wait until team is selected
    if (user?.role !== 'admin') {
      loadTeamReports('', '');
    }
  }, [user, loadTeamReports]);

  useEffect(() => {
    if (user?.role === 'admin' && selectedTeamId) {
      loadTeamReports(selectedDate, selectedTeamId);
    }
  }, [selectedTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Member history
  // ----------------------------------------------------------------
  const handleViewHistory = async (member) => {
    setHistoryMember(member);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const res = await leaderAPI.getMemberHistory(member.user_id, 14);
      setHistoryData(res.data);
    } catch (err) {
      setHistoryData({ error: err.response?.data?.error || 'Failed to load history' });
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryMember(null);
    setHistoryData(null);
  };

  const handleOpenExportDialog = (format, dateFrom, dateTo) => {
    setExportDateFrom(dateFrom);
    setExportDateTo(dateTo);
    setExportDialogOpen(true);
  };

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------
  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    loadTeamReports(date, selectedTeamId);
  };

  const handleRefresh = () => {
    loadTeamReports(selectedDate, selectedTeamId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Nav */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center flex-wrap gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-blue-600">MyNotifier</h1>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <button
              onClick={() => navigate('/report')}
              className="text-xs sm:text-sm text-blue-600 hover:underline px-2 py-1"
            >
              My Report
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-xs sm:text-sm text-gray-500 hover:underline px-2 py-1"
            >
              Dashboard
            </button>
            <span className="text-gray-600 text-xs sm:text-sm px-2">{user?.full_name}</span>
            <button
              onClick={logout}
              className="bg-red-500 text-white px-2 sm:px-3 py-1.5 rounded text-xs sm:text-sm hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Team Leader Dashboard</h2>
            {teamData && (
              <p className="text-gray-500 mt-1 text-sm">
                {teamData.team.team_name} &mdash; {formatDate(teamData.date)}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Admin team picker */}
            {user?.role === 'admin' && teams.length > 0 && (
              <select
                value={selectedTeamId}
                onChange={e => setSelectedTeamId(e.target.value)}
                className="border border-gray-300 rounded px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.team_name}</option>
                ))}
              </select>
            )}

            {/* Date picker */}
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            <button
              onClick={handleRefresh}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded">
            {error}
          </div>
        )}

        {loading && !teamData && (
          <div className="text-center py-20 text-gray-500">Loading team reports...</div>
        )}

        {teamData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left column: summary + member list */}
            <div className="lg:col-span-2 space-y-4">
              {/* Summary bar */}
              <div className="bg-white rounded-lg shadow p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">{teamData.summary.total}</p>
                  <p className="text-xs sm:text-xs text-gray-500 mt-1">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{teamData.summary.submitted}</p>
                  <p className="text-xs text-gray-500 mt-1">Submitted</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-red-500">{teamData.summary.missing}</p>
                  <p className="text-xs text-gray-500 mt-1">Missing</p>
                </div>
                <div className="text-center">
                  <p className={`text-xl sm:text-2xl font-bold ${teamData.summary.isComplete ? 'text-blue-600' : 'text-yellow-500'}`}>
                    {teamData.summary.isComplete ? '✓' : `${teamData.summary.requiredSubmitted}/${teamData.summary.required}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Required</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="bg-white rounded-lg shadow px-4 sm:px-5 py-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span className="truncate">Submission progress</span>
                  <span className="flex-shrink-0 ml-2">
                    {teamData.summary.required > 0
                      ? Math.round((teamData.summary.requiredSubmitted / teamData.summary.required) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${teamData.summary.isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{
                      width: teamData.summary.required > 0
                        ? `${Math.round((teamData.summary.requiredSubmitted / teamData.summary.required) * 100)}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>

              {/* Status banner */}
              {teamData.summary.isComplete ? (
                <div className="bg-green-50 border border-green-300 rounded-lg p-3 sm:p-4 text-green-800 text-xs sm:text-sm font-medium">
                  ✅ All required members have submitted. Reports are locked.
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm">
                  ⏳ Waiting for{' '}
                  <strong>{teamData.summary.required - teamData.summary.requiredSubmitted}</strong>{' '}
                  required member(s) to submit.
                </div>
              )}

              {/* Member cards */}
              <div className="space-y-2">
                {teamData.members.map(member => (
                  <MemberReportCard
                    key={member.user_id}
                    member={member}
                    onViewHistory={handleViewHistory}
                  />
                ))}
              </div>
            </div>

            {/* Right column: tabbed panels */}
            <div className="lg:col-span-1 space-y-4">
              {/* Tab buttons */}
              <div className="flex gap-2 bg-white rounded-lg shadow p-2 flex-wrap">
                <button
                  onClick={() => setRightPanelSection('send')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                    rightPanelSection === 'send'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📤 Send
                </button>
                <button
                  onClick={() => setRightPanelSection('analytics')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                    rightPanelSection === 'analytics'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📊 Analytics
                </button>
                <button
                  onClick={() => setRightPanelSection('notifications')}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                    rightPanelSection === 'notifications'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔔 Alerts
                </button>
                <button
                  onClick={() => setRightPanelSection('history')}
                  disabled={!historyMember}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                    rightPanelSection === 'history'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                  }`}
                  title={!historyMember ? 'Select a member first' : ''}
                >
                  📋 History
                </button>
              </div>

              {/* Send panel */}
              {rightPanelSection === 'send' && (
                <SendSummaryPanel
                  date={teamData?.date}
                  teamId={selectedTeamId ? parseInt(selectedTeamId) : undefined}
                />
              )}

              {/* Analytics panel */}
              {rightPanelSection === 'analytics' && teamData && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">📊 Team Analytics</h3>
                  <TeamAnalyticsPanel
                    teamId={selectedTeamId ? parseInt(selectedTeamId) : undefined}
                    teamName={teamData.team.team_name}
                    onExportClick={handleOpenExportDialog}
                  />
                </div>
              )}

              {/* Notifications panel */}
              {rightPanelSection === 'notifications' && (
                <NotificationHistoryPanel
                  teamId={selectedTeamId ? parseInt(selectedTeamId) : undefined}
                  visible={true}
                />
              )}

              {/* Member history panel */}
              {rightPanelSection === 'history' && (
                historyMember ? (
                  <div className="bg-white rounded-lg shadow">
                    <div className="flex items-center justify-between p-4 border-b">
                      <div>
                        <p className="font-semibold text-gray-900">{historyMember.full_name}</p>
                        <p className="text-xs text-gray-500">Last 14 days history</p>
                      </div>
                      <button
                        onClick={closeHistory}
                        className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                      >
                        &times;
                      </button>
                    </div>

                    <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                      {historyLoading && (
                        <p className="text-gray-500 text-sm text-center py-4">Loading...</p>
                      )}
                      {historyData?.error && (
                        <p className="text-red-500 text-sm">{historyData.error}</p>
                      )}
                      {historyData?.reports && historyData.reports.length === 0 && (
                        <p className="text-gray-400 text-sm text-center py-4">No reports found.</p>
                      )}
                      {historyData?.reports?.map(r => (
                        <div key={r.id} className="border border-gray-100 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-gray-700">{formatDate(r.report_date)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              r.status === 'submitted' || r.status === 'locked'
                                ? 'bg-green-100 text-green-700'
                                : r.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {r.status}
                            </span>
                          </div>
                          {r.yesterday_text && (
                            <div className="mb-1">
                              <p className="text-xs text-gray-400 font-semibold uppercase mb-0.5">Yesterday</p>
                              <p className="text-xs text-gray-700 whitespace-pre-wrap">{r.yesterday_text}</p>
                            </div>
                          )}
                          {r.today_text && (
                            <div>
                              <p className="text-xs text-gray-400 font-semibold uppercase mb-0.5">Today</p>
                              <p className="text-xs text-gray-700 whitespace-pre-wrap">{r.today_text}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow p-6 text-center text-gray-400">
                    <p className="text-4xl mb-3">👤</p>
                    <p className="text-sm">Click <strong>History</strong> on any member card to view their report history here.</p>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </main>

      {/* Export Dialog */}
      <ExportDialog
        teamId={selectedTeamId ? parseInt(selectedTeamId) : 0}
        teamName={teamData?.team.team_name || 'Team'}
        dateFrom={exportDateFrom}
        dateTo={exportDateTo}
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </div>
  );
};

export default LeaderDashboardPage;
